import { Component, ElementRef, HostListener, OnDestroy, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, NavigationEnd, ParamMap, Router, RouterEvent, RouterModule } from '@angular/router';
import { Store, select } from '@ngrx/store';
import { distinctUntilChanged, filter, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { fadeInOutYAnimation } from 'src/app/shared/animations/animations';
import { FilterReportComponent } from 'src/app/shared/components/filter-report/filter-report.component';
import { menuItemsForUser } from 'src/app/shared/constant/menu_items.constant';
import { sessionAuthData, sessionUserData } from 'src/app/shared/interfaces/states-data';
import { ExperiencesService } from 'src/app/shared/services/experiences/experiences.service';
import { UserService } from 'src/app/shared/services/user/user.service';
import { __clearSessionAuth } from 'src/app/shared/states/session-auth/session-auth.actions';
import { __clearSessionUser } from 'src/app/shared/states/session-user/session-user.actions';
import { TokenRefreshSchedulerService } from 'src/app/shared/services/auth/token-refresh-scheduler.service';
import { TokenRefreshCoordinatorService } from 'src/app/shared/services/auth/token-refresh-coordinator.service';
import { ModalActionService } from 'src/app/shared/components/modal-action/modal-action.service';
import { PermissionInspectorService } from 'src/app/shared/services/permission-inspector/permission-inspector.service';
import { CashierSessionService } from 'src/app/shared/services/cashier-session/cashier-session.service';
import { getSessionAuthData } from 'src/app/shared/states/session-auth/session-auth.selectors';
import { getSessionUserData } from 'src/app/shared/states/session-user/session-user.selectors';
import { SharedModule } from 'src/app/shared/shared.module';
import { selectLogoIsoUrl, selectLogoMainUrl, selectLogoWhiteUrl } from 'src/app/shared/states/white-label/white-label.selectors';

@Component({
  standalone: true,
  imports: [SharedModule, RouterModule],
  selector: 'app-app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  animations: [
    fadeInOutYAnimation
  ]
})
export class AppLayoutComponent implements AfterViewInit, OnDestroy, OnInit {

  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  behaviourStates: any = {
    sidebarOpen: false
  }

  // User States
  sessionUserState: sessionUserData = {};
  sessionAuthState: sessionAuthData = { loggedIn: false };

  userMenuOpen = false;

  // Caja (A-ARQ-005): con caja abierta, el menú ofrece cerrarla desde cualquier pantalla.
  cashierSessionOpen = false;
  showCashierCloseModal = false;

  // Sin correo todavía: arranca sin los subítems reservados a operadores (Mantenimiento) y
  // se recalcula cuando llega el usuario.
  menuItems = menuItemsForUser(undefined);

  filterReports: string[] = [];
  refreshReports = false;
  // @ViewChild('FilterReportComponent2', { static: false })
  // FilterReportComponent!: FilterReportComponent;
  routeExperienceId: string | null = null;
  reportVisibility = {
    menu: false,
    tickets: false,
    experienceBoxOffice: false,
    globalBoxOffice: false,
    deletedTickets: false
  };
  sidebarReady = false;
  activeMenuRoutes: Record<string, boolean> = {};
  activeSubMenuRoutes: Record<string, boolean> = {};
  private routeExperienceIdTimer: ReturnType<typeof setTimeout> | null = null;
  private reportVisibilityTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingExperiencesInterval: ReturnType<typeof setInterval> | null = null;
  private sidebarReadyTimer: ReturnType<typeof setTimeout> | null = null;
  private activeRouteTimer: ReturnType<typeof setTimeout> | null = null;

  logoMain$: Observable<string>;
  logoIso$: Observable<string>;
  logoWhite$: Observable<string>;



  constructor(
    private el: ElementRef,
    private store: Store<any>,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private experienceService: ExperiencesService,
    private userService: UserService,
    private tokenRefreshScheduler: TokenRefreshSchedulerService,
    private tokenRefreshCoordinator: TokenRefreshCoordinatorService,
    private modalAction: ModalActionService,
    public permissionInspector: PermissionInspectorService,
    private cashierSessionService: CashierSessionService
  ) {
    this.logoMain$ = this.store.select(selectLogoMainUrl);
    this.logoIso$ = this.store.select(selectLogoIsoUrl);
    this.logoWhite$ = this.store.select(selectLogoWhiteUrl);
    this._activatedRoute.parent?.paramMap
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((params: ParamMap) => {
        if (params.get('id_experience')) {
          this.scheduleRouteExperienceIdUpdate(params.get('id_experience'));
        } else if (this._activatedRoute.snapshot.firstChild?.firstChild?.firstChild?.params['id_experience']) {
          this.scheduleRouteExperienceIdUpdate(this._activatedRoute.snapshot.firstChild?.firstChild?.firstChild?.params['id_experience']);
        } else {
          this.scheduleRouteExperienceIdUpdate(null);
        }
      });

    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((event: any) => {
        // recapturar el id de la experiencia si existe
        if (event.url.includes('experiences')) {
          if (this._activatedRoute.snapshot.firstChild?.firstChild?.firstChild?.params['id_experience']) {
            this.scheduleRouteExperienceIdUpdate(this._activatedRoute.snapshot.firstChild?.firstChild?.firstChild?.params['id_experience']);
          } else {
            this.scheduleRouteExperienceIdUpdate(null);
          }
        } else {
          this.scheduleRouteExperienceIdUpdate(null);
        }

        this.scheduleActiveRouteStateUpdate(event.urlAfterRedirects || event.url);

      });

    // Al recibir cambios del reducer - User (p. ej. tras impersonación o refresh de token)
    this.store
      .pipe(
        select(getSessionUserData),
        distinctUntilChanged(
          (a, b) => (a?.user_id ?? '') === (b?.user_id ?? '') && (a?.data_user_hash ?? '') === (b?.data_user_hash ?? ''),
        ),
        takeUntil(this.ngUnsubscribe),
      )
      .subscribe((data: sessionUserData) => {
        setTimeout(() => {
          this.sessionUserState = data;
          // Copia nueva por usuario (impersonación incluida); getPendingExperiences vuelve
          // a escribir las notificaciones sobre ella.
          this.menuItems = menuItemsForUser(data?.email);
          this.getPendingExperiences();
        }, 0);
      });

    this.store.pipe(select(getSessionAuthData), takeUntil(this.ngUnsubscribe))
      .subscribe((data: sessionAuthData) => {
        setTimeout(() => {
          this.sessionAuthState = data;
          this.scheduleReportVisibilityUpdate();
          // Solo quien opera taquilla puede tener caja: así el resto no consulta /active/me.
          if (this.validatePermission('OPERAR_TAQUILLA')) {
            this.cashierSessionService.refreshActive().subscribe({ error: () => {} });
          }
        }, 0);

      })

    this.cashierSessionService.activeSession$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((session) => {
        this.cashierSessionOpen = !!session;
      });
  }

  ngOnInit(): void {
    this.behaviourStates = JSON.parse(localStorage.getItem('states[app-layout]') || JSON.stringify(this.behaviourStates));

    this.getPendingExperiences();

    // Obtener el numero de experiencias pendientes de aprobación con un timer infinito
    this.pendingExperiencesInterval = setInterval(() => {
      this.getPendingExperiences();
    }, 30000);
  }

  ngAfterViewInit(): void {
    this.sidebarReadyTimer = setTimeout(() => {
      this.sidebarReadyTimer = null;
      this.updateActiveRouteState(this._router.url);
      this.sidebarReady = true;
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.routeExperienceIdTimer) {
      clearTimeout(this.routeExperienceIdTimer);
    }
    if (this.reportVisibilityTimer) {
      clearTimeout(this.reportVisibilityTimer);
    }
    if (this.pendingExperiencesInterval) {
      clearInterval(this.pendingExperiencesInterval);
    }
    if (this.sidebarReadyTimer) {
      clearTimeout(this.sidebarReadyTimer);
    }
    if (this.activeRouteTimer) {
      clearTimeout(this.activeRouteTimer);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private scheduleRouteExperienceIdUpdate(routeExperienceId: string | null) {
    if (this.routeExperienceIdTimer) {
      clearTimeout(this.routeExperienceIdTimer);
    }

    this.routeExperienceIdTimer = setTimeout(() => {
      this.routeExperienceIdTimer = null;
      this.routeExperienceId = routeExperienceId;
      this.scheduleReportVisibilityUpdate();
    }, 0);
  }

  private scheduleReportVisibilityUpdate() {
    if (this.reportVisibilityTimer) {
      clearTimeout(this.reportVisibilityTimer);
    }

    this.reportVisibilityTimer = setTimeout(() => {
      this.reportVisibilityTimer = null;
      this.reportVisibility = {
        menu: this.validatePermissionArray([
          'REPORTE_BOLETOS_EXP',
          'REPORTE_TAQUILLA_EXP',
          'REPORTE_TAQUILLA_GLOBAL_EXP',
          'REPORTE_BOLETOS_ELIMINADOS_EXP'
        ]),
        tickets: this.validatePermission('REPORTE_BOLETOS_EXP'),
        experienceBoxOffice: this.validatePermission('REPORTE_TAQUILLA_EXP'),
        globalBoxOffice: this.validatePermission('REPORTE_TAQUILLA_GLOBAL_EXP'),
        deletedTickets: this.validatePermission('REPORTE_BOLETOS_ELIMINADOS_EXP')
      };
    }, 0);
  }

  private scheduleActiveRouteStateUpdate(currentUrl: string) {
    if (this.activeRouteTimer) {
      clearTimeout(this.activeRouteTimer);
    }

    this.activeRouteTimer = setTimeout(() => {
      this.activeRouteTimer = null;
      this.updateActiveRouteState(currentUrl);
    }, 0);
  }

  private updateActiveRouteState(currentUrl: string) {
    const normalizedCurrentUrl = (currentUrl || '').split('?')[0];
    const activeMenuRoutes: Record<string, boolean> = {};
    const activeSubMenuRoutes: Record<string, boolean> = {};

    this.menuItems.forEach((item: any) => {
      const itemRouteKey = item.routerLink || '';
      const normalizedItemRoute = this.normalizeRouteLink(item.routerLink);

      if (itemRouteKey) {
        activeMenuRoutes[itemRouteKey] = !!normalizedItemRoute &&
          (normalizedCurrentUrl === normalizedItemRoute || normalizedCurrentUrl.startsWith(`${normalizedItemRoute}/`));
      }

      item.subItems.forEach((subItem: any) => {
        const subItemRouteKey = subItem.routerLink || '';
        if (!subItemRouteKey) {
          return;
        }

        const normalizedSubItemRoute = this.normalizeRouteLink(subItem.routerLink);
        activeSubMenuRoutes[subItemRouteKey] = !!normalizedSubItemRoute &&
          (normalizedCurrentUrl === normalizedSubItemRoute || normalizedCurrentUrl.startsWith(`${normalizedSubItemRoute}/`));
      });
    });

    this.activeMenuRoutes = activeMenuRoutes;
    this.activeSubMenuRoutes = activeSubMenuRoutes;
  }

  /* Cierre de caja desde el menú lateral. En móvil se cierra el cajón para que el modal quede solo. */
  openCashierCloseModal() {
    this.showCashierCloseModal = true;
    if (window.innerWidth < 1024) {
      this.toggleSidebar(true);
    }
  }

  /* Flipflop sidebar */
  toggleSidebar(close = false) {
    if (close) {
      this.behaviourStates.sidebarOpen = false;
    } else {
      this.behaviourStates.sidebarOpen = !this.behaviourStates.sidebarOpen;
    }

    localStorage.setItem('states[app-layout]', JSON.stringify(this.behaviourStates));
  }

  /* Flipflop usermenu */
  toggleUserMenu(close = false) {
    if (close) {
      this.userMenuOpen = false;
    } else {
      this.userMenuOpen = !this.userMenuOpen;
    }
  }

  /* Al presionar escape cerrar el sidebar */
  @HostListener('document:keydown.escape')
  onKeydownHandler() {
    this.toggleSidebar(true);
    this.toggleUserMenu(true);
  }

  /* Al presionar escape cerrar el sidebar */
  @HostListener('document:click', ["$event"])
  onClickHandler(event: any) {

    // Referencia al boton del menu del usuario
    const userMenuElement = this.el.nativeElement.querySelector('#user-menu');
    const clickedElement = event.target as HTMLElement;

    if (userMenuElement) {

      // Cerrar el menu de usuario si se da click en cualquier otro elemento
      if (clickedElement !== userMenuElement && !userMenuElement.contains(clickedElement)) {
        this.toggleUserMenu(true);
      }
    }
  }

  /* Al entra en md */
  @HostListener('window:resize', ["$event"])
  onresize(event: any) {
    // <<
    if (window.innerWidth < 1024) {
      this.toggleSidebar(true);
    }
  }

  /* Limpiar la sesion de la aplicacion */
  // B-SEC-005: pega al backend `/user/logout` para revocar el refresh token actual
  // (rotación + revocación server-side). Es tolerante: si la red/backend falla,
  // igual limpia el estado local y redirige para no dejar al usuario atrapado.
  async clearSession() {
    // Logout intencional: suprime el modal de "Sesión expirada" ante los 401
    // residuales de requests en vuelo mientras se desmonta la sesión.
    this.tokenRefreshCoordinator.markIntentionalLogout();
    try {
      let refreshToken: string | null = null;
      try {
        refreshToken = JSON.parse(localStorage.getItem('sessionAuthState') || '{}').refresh_token || null;
      } catch {
        refreshToken = null;
      }
      if (refreshToken) {
        await firstValueFrom(this.userService.logout(refreshToken));
      }
    } catch {
      // Tolerante: si falla, igual cerrar local.
    }

    // Limpiar storage local explícito
    try {
      localStorage.removeItem('sessionAuthState');
      localStorage.removeItem('sessionUserState');
    } catch {
      // ignorar
    }

    // Borrar sesion del reducer (también limpia user state)
    this.store.dispatch(__clearSessionAuth());
    this.store.dispatch(__clearSessionUser());

    // B-SEC-005 proactive refresh: cancelar timer + resetear coordinator para
    // que no quede un refresh fantasma disparándose post-logout.
    this.tokenRefreshScheduler.cancel();
    this.tokenRefreshCoordinator.reset();

    // Navegar al login
    this._router.navigate(['/auth/login']);

    // Aviso de cierre exitoso (reemplaza al modal de "Sesión expirada" que antes
    // aparecía por los 401 residuales del teardown).
    this.modalAction.openDialog({
      title: 'Sesión finalizada',
      message: 'Tu sesión se cerró exitosamente.',
      icon: 'success',
    });
  }
  goToAccount() {
    this._router.navigate(['home/account/profile']);
  }
  /** Primer permiso cuando `access` es string o string[] (p. ej. reportes desde submenú). */
  subItemAccessReportKey(access: string | string[] | null | undefined): string {
    if (access == null) {
      return '';
    }
    return Array.isArray(access) ? (access[0] ?? '') : access;
  }

  openModalFilterReport(report: string) {
    this.filterReports = [report];
    this.refreshReports = false;

    setTimeout(() => {
      this.refreshReports = true;

    }, 100);
    setTimeout(() => {
      const getElementById = document.getElementById('botonShowReporte_1');
      if (getElementById) {
        getElementById.click();
      }
    }, 200);


  }

  private normalizeRouteLink(routerLink: string | null | undefined): string {
    if (!routerLink) {
      return '';
    }

    if (routerLink.startsWith('/')) {
      return routerLink;
    }

    if (routerLink.startsWith('./')) {
      return `/home/${routerLink.slice(2)}`;
    }

    return `/${routerLink}`;
  }

  isRouteActive(routerLink: string | null | undefined): boolean {
    if (!routerLink) {
      return false;
    }

    return !!this.activeSubMenuRoutes[routerLink];
  }

  isMenuItemActive(item: any): boolean {
    return !!this.activeMenuRoutes[item?.routerLink || ''];
  }

  subMenuTestId(label: string): string {
    return 'app-layout-submenu-link-' + label.toLowerCase().replace(/\s+/g, '-');
  }

  validatePermissionArray(permissionsToCheck: string[]): boolean {
    if (this.sessionAuthState.is_super_admin) {
      return true;
    }
    
    const userPermissions = this.sessionAuthState.permissions || [];
    const acceptedPermissions: string[] = [];

    permissionsToCheck.forEach(permission => {
      if (userPermissions.includes(permission)) {
        acceptedPermissions.push(permission);
      }
    });

    if (acceptedPermissions.length == 0) {
      return false;
    }

    if (acceptedPermissions.length == 1 && acceptedPermissions[0] == 'REPORTE_TAQUILLA_EXP') {
      return !!this.routeExperienceId;
    }

    return true;
  }

  validatePermission(permission: string): boolean {
    if (this.sessionAuthState.is_super_admin) {
      return true;
    }
    
    if (!permission || !this.sessionAuthState.permissions?.includes(permission)) {
      return false;
    }

    if (permission == 'REPORTE_TAQUILLA_EXP' || permission == 'REPORTE_BOLETOS_ELIMINADOS_EXP') {
      return !!this.routeExperienceId;
    }

    return true;
  } 
  getPendingExperiences() {
    this.experienceService.getPendingExperiences().subscribe((data: any) => {
      setTimeout(() => {
        const getMenuItems = this.menuItems.find((item: any) => item.label == 'Experiencias');

        if (getMenuItems) {
          const pendingItem = getMenuItems.subItems.find((subItem: any) => subItem.label == 'Por Aprobar');
          if (pendingItem) {
            pendingItem.notification = data;
            console.log(pendingItem.notification);
          }
        }
      }, 0);
    });
  }

}
